import { Loader2, Play, Save, Square } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/auth-context";
import { useTimezone } from "@/contexts/timezone-context";
import { useCiJobOverviews } from "@/hooks/use-ci";
import { useMctestInfo, useTriggerMctest } from "@/hooks/use-mctest";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  useCancelCiJob,
  useMyMachines,
  useNightlyConfig,
  useSetNightlyConfig,
  useTriggerCryodTest,
  useTriggerOptions,
  useTriggerPerf,
} from "@/hooks/use-trigger";
import { PERF_BACKEND_LABELS } from "@/lib/restore-perf";
import type {
  CiJobOverview,
  Machine,
  MctestTriggerResponse,
  NightlyConfigState,
  ScheduledRun,
  TriggerOptions,
} from "@/lib/types";

// Every test that can be run from this page. The value is the job trigger it
// queues under; `refLabel` names what the job's release_tag holds for it.
// Adding a test means one entry here plus its form below.
// First entry is the page's default.
const TEST_KINDS = [
  // Not a trigger: what the scheduled nightly and weekend runs run, and their recent runs below.
  { value: "nightly", label: "Nightly", refLabel: "Ref" },
  { value: "cryod-test", label: "Cryod test", refLabel: "Mode" },
  { value: "perf", label: "Performance test", refLabel: "Cell" },
  { value: "mctest", label: "Mooncake test", refLabel: "Branch" },
] as const;

// Jobs the Stop button applies to: not started yet, or still running.
const ACTIVE_JOB_STATES = new Set(["queued", "claimed", "running"]);

// Enter in a text or number box must not queue a job; only the button does.
function noEnterSubmit(event: KeyboardEvent<HTMLFormElement>) {
  if (event.key === "Enter" && event.target instanceof HTMLInputElement) event.preventDefault();
}

// The "Start at" field and its instant for the request (null = start now). The
// wall time is read in the page's timezone setting, like every time shown here.
function useStartAt() {
  const { timezone } = useTimezone();
  const [value, setValue] = useState("");
  const date = value ? new Date(timezone === "utc" ? `${value}:00Z` : value) : null;
  // An unparseable value goes out as typed: the API refuses it instead of running now.
  const iso = date === null ? null : Number.isNaN(date.getTime()) ? value : date.toISOString();
  // The picker's floor: the current minute in the same zone, so past times are greyed out.
  const offsetMinutes = timezone === "utc" ? 0 : new Date().getTimezoneOffset();
  const min = new Date(Date.now() - offsetMinutes * 60_000).toISOString().slice(0, 16);
  // Cleared after a submit: re-sending a start time that has since passed would be refused.
  const clear = () => setValue("");
  return {
    value,
    setValue,
    clear,
    iso,
    min,
    zoneLabel: timezone === "utc" ? "UTC" : "local time",
  };
}

function StartAtPicker({ startAt }: { startAt: ReturnType<typeof useStartAt> }) {
  return (
    <label className="space-y-1 text-sm">
      <span className="text-muted-foreground">Start at ({startAt.zoneLabel}, empty = now)</span>
      <Input
        type="datetime-local"
        className="w-56"
        min={startAt.min}
        max="9999-12-31T23:59"
        value={startAt.value}
        onChange={(event) => startAt.setValue(event.target.value)}
      />
    </label>
  );
}

function SkipEodCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-sm">
      <Checkbox checked={checked} onCheckedChange={(value) => onChange(value === true)} />
      Skip the EOD release of every machine this run uses, from when it's queued, including ones it
      takes mid-run (each skip lasts to 09:00)
    </label>
  );
}

function toggled(values: string[], value: string, on: boolean): string[] {
  return on ? [...new Set([...values, value])] : values.filter((item) => item !== value);
}

function machineLabel(machine: Machine): string {
  return `${machine.name} · ${machine.gpu_count}×${machine.gpu_type}`;
}

export function CiTriggerPage() {
  usePageTitle("Trigger");
  const [kind, setKind] = useState<string>(TEST_KINDS[0].value);
  const selected = TEST_KINDS.find((test) => test.value === kind) ?? TEST_KINDS[0];

  return (
    <div className="mx-auto max-w-screen-xl space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Trigger</h1>
        <p className="text-sm text-muted-foreground">
          Run a test suite on demand, as its own fleet job.
        </p>
      </div>

      <Select value={kind} onValueChange={(value) => value && setKind(value)}>
        <SelectTrigger className="w-56 gap-1">
          <span className="text-muted-foreground">Test:</span>
          <SelectValue>
            {(current) =>
              TEST_KINDS.find((test) => test.value === current)?.label ?? String(current)
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {TEST_KINDS.map((test) => (
            <SelectItem key={test.value} value={test.value}>
              {test.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selected.value === "mctest" && <MooncakeTestTrigger />}
      {selected.value === "cryod-test" && <CryodTestTrigger />}
      {selected.value === "perf" && <PerfTrigger />}
      {selected.value === "nightly" && (
        <>
          <NightlySettings
            run="nightly"
            title="Nightly"
            description="What the next nightly runs (00:15–00:45 Israel time, Monday–Thursday); the fields show the values in use. Edit and save to change them. A nightly already running keeps what it launched with."
          />
          <NightlySettings
            run="weekend"
            title="Weekend run (Friday 00:15 → Sunday morning)"
            description="Its own settings. Unpinned SDK engines offer every registry version as a candidate within the 56-hour budget, new releases included."
          />
        </>
      )}
      <RecentJobs trigger={selected.value} label={selected.label} refLabel={selected.refLabel} />
    </div>
  );
}

function SubmitButton({
  pending,
  disabled,
  label,
  icon: Icon = Play,
}: {
  pending: boolean;
  disabled?: boolean;
  label: string;
  icon?: typeof Play;
}) {
  return (
    <Button type="submit" size="sm" disabled={pending || disabled}>
      {pending ? (
        <Loader2 className="mr-1.5 size-3 animate-spin" />
      ) : (
        <Icon className="mr-1.5 size-3" />
      )}
      {label}
    </Button>
  );
}

function QueuedNotice({
  queued,
  runIsJob = true,
}: {
  queued: MctestTriggerResponse;
  runIsJob?: boolean;
}) {
  const { formatShortTimestamp } = useTimezone();
  const jobIds = queued.job_ids ?? [queued.job_id];
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-sm">
      Queued {jobIds.length > 1 ? "jobs" : "job"}{" "}
      {jobIds.map((jobId, index) => (
        <span key={jobId}>
          {index > 0 && ", "}
          <Link className="font-mono underline" to={`/ci/jobs/${jobId}`}>
            {jobId}
          </Link>
        </span>
      ))}
      {queued.not_before && `, starting ${formatShortTimestamp(queued.not_before)}`}.{" "}
      {!runIsJob ? (
        "Its CI runs appear on the job page as the orchestrator publishes them."
      ) : jobIds.length > 1 ? (
        "Each CI run appears under its job id once results publish; the job pages show the phases meanwhile."
      ) : (
        <>
          The CI run{" "}
          <Link className="font-mono underline" to={`/ci/${queued.run_id}`}>
            {queued.run_id}
          </Link>{" "}
          appears once results publish; the job page shows the phases meanwhile.
        </>
      )}
    </div>
  );
}

function ErrorText({ error }: { error: unknown }) {
  return (
    <span className="text-xs text-destructive">
      {error instanceof Error ? error.message : "Failed"}
    </span>
  );
}

function NotConfigured({ what }: { what: string }) {
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
      {what} is not configured on this fleet host.
    </div>
  );
}

// An image tag from the registry's listing, searchable by name; a text box
// only when the listing failed (the tag still has to exist in the registry).
// `fallback` names what an empty pick means and is the default entry.
function ImageTagPicker({
  label,
  tags,
  tagsError,
  fallback,
  value,
  onChange,
}: {
  label: string;
  tags: string[];
  tagsError: string;
  fallback: string;
  value: string;
  onChange: (next: string) => void;
}) {
  if (tags.length === 0) {
    return (
      <label className="space-y-1 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <Input
          className="w-64 font-mono"
          placeholder={fallback}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        {tagsError && (
          <span className="block text-xs text-amber-500">
            Couldn't list the registry ({tagsError}).
          </span>
        )}
      </label>
    );
  }
  const choices = tags.includes(fallback) ? tags : [fallback, ...tags];
  return (
    <label className="space-y-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Combobox
        value={value || fallback}
        onValueChange={(next) => next && onChange(next === fallback ? "" : next)}
        options={choices}
        placeholder={fallback}
        searchPlaceholder="Search tags…"
        emptyText="No matching tag"
        className="w-64 font-mono text-xs"
      />
    </label>
  );
}

const MOONCAKE_TAG_DERIVED = "derived from the cryod tag";

// The cryod image tag and the Mooncake (storage engine) image tag; an empty
// Mooncake pick lets bench derive it from the cryod tag.
function ImageTagPickers({
  options,
  imageTag,
  onImageTag,
  mooncakeTag,
  onMooncakeTag,
}: {
  options: TriggerOptions;
  imageTag: string;
  onImageTag: (next: string) => void;
  mooncakeTag: string;
  onMooncakeTag: (next: string) => void;
}) {
  return (
    <>
      <ImageTagPicker
        label="Cryod image tag"
        tags={options.cryod_tags}
        tagsError={options.cryod_tags_error}
        fallback="latest-ci"
        value={imageTag}
        onChange={onImageTag}
      />
      <ImageTagPicker
        label="Mooncake image tag"
        tags={options.mooncake_tags}
        tagsError={options.mooncake_tags_error}
        fallback={MOONCAKE_TAG_DERIVED}
        value={mooncakeTag}
        onChange={onMooncakeTag}
      />
    </>
  );
}

// Catalog tests as a filterable checkbox list, with pick / clear for the shown ones.
function TestPicker({
  label,
  tests,
  picked,
  onChange,
}: {
  label: string;
  tests: TriggerOptions["tests"];
  picked: string[];
  onChange: (next: string[]) => void;
}) {
  const [filter, setFilter] = useState("");
  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return needle
      ? tests.filter(
          (test) =>
            test.name.toLowerCase().includes(needle) ||
            test.tags.some((tag) => tag.toLowerCase().includes(needle)),
        )
      : tests;
  }, [tests, filter]);
  const shownPicked = visible.filter((test) => picked.includes(test.name)).length;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          {label}
          {picked.length > 0 && ` (${picked.length} picked)`}
        </span>
        <Input
          className="w-64"
          placeholder="filter by name or tag (e.g. cpu, webhook)"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={visible.length === 0 || shownPicked === visible.length}
          onClick={() => onChange([...new Set([...picked, ...visible.map((test) => test.name)])])}
        >
          Pick all {visible.length} shown
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={shownPicked === 0}
          onClick={() => {
            const shown = new Set(visible.map((test) => test.name));
            onChange(picked.filter((name) => !shown.has(name)));
          }}
        >
          Clear shown
        </Button>
      </div>
      <div className="max-h-56 overflow-auto rounded-md border p-2">
        {visible.map((test) => (
          <label key={test.name} className="flex items-center gap-1.5 py-0.5 text-sm">
            <Checkbox
              checked={picked.includes(test.name)}
              onCheckedChange={(checked) => onChange(toggled(picked, test.name, checked === true))}
            />
            <span className="font-mono text-xs">{test.name}</span>
            <span className="text-xs text-muted-foreground">{test.engine}</span>
            {test.tags.includes("cpu") && (
              <span className="rounded border px-1 text-[10px] text-muted-foreground">cpu</span>
            )}
          </label>
        ))}
        {visible.length === 0 && (
          <span className="text-xs text-muted-foreground">No test matches.</span>
        )}
      </div>
    </div>
  );
}

// The requester's held machines as a checkbox list (own-machine runs).
function MachinePicker({
  machines,
  error,
  picked,
  onChange,
}: {
  machines: Machine[] | undefined;
  error: unknown;
  picked: string[];
  onChange: (next: string[]) => void;
}) {
  if (error) return <ErrorText error={error} />;
  if (machines === undefined) return <Skeleton className="h-9 w-72" />;
  if (machines.length === 0) {
    return <span className="text-sm text-muted-foreground">You hold no machines right now.</span>;
  }
  return (
    <div className="flex flex-wrap gap-3">
      {machines.map((machine) => (
        <label key={machine.id} className="flex items-center gap-1.5 text-sm">
          <Checkbox
            checked={picked.includes(machine.name)}
            onCheckedChange={(checked) => onChange(toggled(picked, machine.name, checked === true))}
          />
          <span className="font-mono text-xs">{machineLabel(machine)}</span>
        </label>
      ))}
    </div>
  );
}

function MooncakeTestTrigger() {
  const { data: info, isLoading, error } = useMctestInfo();
  const trigger = useTriggerMctest();
  const [ref, setRef] = useState("main");
  const startAt = useStartAt();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mooncake test</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <ErrorText error={error} />
        ) : isLoading || info === undefined ? (
          <Skeleton className="h-9 w-full" />
        ) : !info.enabled ? (
          <NotConfigured what="Mooncake test" />
        ) : (
          <form
            className="flex flex-wrap items-end gap-3"
            onKeyDown={noEnterSubmit}
            onSubmit={(event) => {
              event.preventDefault();
              trigger.mutate(
                { mooncakeRef: ref.trim() || "main", startAt: startAt.iso },
                { onSuccess: startAt.clear },
              );
            }}
          >
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Mooncake branch</span>
              <Select value={ref} onValueChange={(value) => value && setRef(value)}>
                <SelectTrigger className="w-72 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">main</SelectItem>
                  {info.branches
                    .filter((branch) => branch !== "main")
                    .map((branch) => (
                      <SelectItem key={branch} value={branch}>
                        {branch}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </label>
            {info.branches_error && (
              <span className="text-xs text-amber-500">
                Couldn't list branches with images ({info.branches_error}); only main is offered.
              </span>
            )}
            <StartAtPicker startAt={startAt} />
            <SubmitButton
              pending={trigger.isPending}
              label={startAt.iso ? "Schedule Mooncake test" : "Run Mooncake test"}
            />
            {trigger.isError && <ErrorText error={trigger.error} />}
          </form>
        )}
        {trigger.isSuccess && <QueuedNotice queued={trigger.data} />}
      </CardContent>
    </Card>
  );
}

// What the scheduled cryod nightly (or weekend run) runs, read at its next launch; empty = the default.
// Versions are typed as the policy writes them: "latest, latest-1" or a pinned tag.
function NightlySettings({
  run,
  title,
  description,
}: {
  run: ScheduledRun;
  title: string;
  description: ReactNode;
}) {
  const { data: options, isLoading, error } = useTriggerOptions();
  const { data: state, error: stateError } = useNightlyConfig(run);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error || stateError ? (
          <ErrorText error={error ?? stateError} />
        ) : isLoading || options === undefined || state === undefined ? (
          <Skeleton className="h-9 w-full" />
        ) : !options.enabled ? (
          <NotConfigured what="The cryod nightly" />
        ) : (
          <NightlyForm run={run} options={options} state={state} description={description} />
        )}
      </CardContent>
    </Card>
  );
}

// A version class (latest, latest-N, oldest, middle) or a version, as the registry
// version it names; `known` is ascending, newest last.
function resolveVersion(versionClass: string, known: string[]): string | undefined {
  if (known.includes(versionClass)) return versionClass;
  if (versionClass === "oldest") return known[0];
  if (versionClass === "middle") return known[Math.floor(known.length / 2)];
  const offset = versionClass === "latest" ? 0 : Number(/^latest-(\d+)$/.exec(versionClass)?.[1]);
  return Number.isNaN(offset) ? undefined : known[known.length - 1 - offset];
}

function resolveVersions(versionClasses: string[], known: string[]): string[] {
  const resolved = versionClasses.map((versionClass) => resolveVersion(versionClass, known));
  return [...new Set(resolved.filter((version): version is string => version !== undefined))];
}

function sameVersions(left: string[], right: string[]): boolean {
  return [...left].sort().join() === [...right].sort().join();
}

function NightlyForm({
  run,
  options,
  state,
  description,
}: {
  run: ScheduledRun;
  options: TriggerOptions;
  state: NightlyConfigState;
  description: ReactNode;
}) {
  const save = useSetNightlyConfig(run);
  const saved = state.config;
  // Every field shows the value in use; one equal to the default saves as "default"
  // (empty), so the run keeps following its default (the policy's newest version, or
  // the weekend's every registry version).
  const [imageTag, setImageTag] = useState(saved.image_tag || state.default_image_tag);
  // A saved tier the nightly no longer offers is left out, so saving clears it.
  // (No tier list means the catalog didn't load: keep them all.)
  const savedTiers = saved.tiers.filter(
    (tier) => options.nightly.tiers.length === 0 || options.nightly.tiers.includes(tier),
  );
  const [tiers, setTiers] = useState(savedTiers.length > 0 ? savedTiers : state.default_tiers);
  const [versionPicks, setVersionPicks] = useState<Record<string, string[]>>({});
  // Per engine run by the picked tiers: the registry's versions, and the ones the policy runs.
  const policyClasses: Record<string, string[]> = {};
  for (const tier of tiers) {
    for (const [engine, classes] of Object.entries(options.nightly.policy_versions[tier] ?? {})) {
      policyClasses[engine] = [...new Set([...(policyClasses[engine] ?? []), ...classes])];
    }
  }
  // Only SDK engines take registry versions; the others run the policy's (e.g. default).
  const engines = Object.keys(policyClasses)
    .filter((engine) => engine.endsWith("-sdk"))
    .sort();
  const policyOnlyEngines = Object.keys(policyClasses)
    .filter((engine) => !engine.endsWith("-sdk"))
    .sort();
  const known = (engine: string): string[] => options.nightly.versions[engine] ?? [];
  const policyPick = (engine: string): string[] =>
    resolveVersions(policyClasses[engine] ?? [], known(engine));
  // What an unpinned engine runs: the weekend's every registry version, else the policy's pick.
  const defaultPick = (engine: string): string[] =>
    state.default_versions[engine] ?? policyPick(engine);
  // A saved pin the registry list can't resolve (or no list) is kept and shown as saved.
  const unresolved = (engine: string): string[] =>
    (saved.versions[engine] ?? []).filter(
      (version) => resolveVersion(version, known(engine)) === undefined,
    );
  const picked = (engine: string): string[] =>
    versionPicks[engine] ??
    (saved.versions[engine]
      ? [...resolveVersions(saved.versions[engine], known(engine)), ...unresolved(engine)]
      : defaultPick(engine));
  const shown = (engine: string): string[] => [...known(engine), ...unresolved(engine)];
  // An engine left untouched saves exactly what was saved (latest stays latest).
  const untouched = (engine: string): string[] | undefined =>
    versionPicks[engine] === undefined ? saved.versions[engine] : undefined;
  const isDefaultTiers = sameVersions(tiers, state.default_tiers);
  // The nightly's default tiers first, in their order, then the rest.
  const orderedTiers = [
    ...state.default_tiers.filter((tier) => options.nightly.tiers.includes(tier)),
    ...options.nightly.tiers.filter((tier) => !state.default_tiers.includes(tier)),
  ];
  const config = {
    image_tag: imageTag.trim() === state.default_image_tag ? "" : imageTag.trim(),
    tiers: isDefaultTiers ? [] : tiers,
    versions: Object.fromEntries([
      // Pins for engines the form shows no picker for are kept as saved.
      ...Object.entries(saved.versions).filter(([engine]) => !engines.includes(engine)),
      ...engines
        .filter((engine) => untouched(engine) || !sameVersions(picked(engine), defaultPick(engine)))
        .map((engine) => [engine, untouched(engine) ?? picked(engine)]),
    ]),
  };
  const changed = JSON.stringify(config) !== JSON.stringify(saved);
  const noVersions = engines.filter(
    (engine) => known(engine).length > 0 && picked(engine).length === 0,
  );

  return (
    <form
      className="space-y-3"
      onKeyDown={noEnterSubmit}
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(config, { onSuccess: () => setVersionPicks({}) });
      }}
    >
      <p className="text-sm text-muted-foreground">{description}</p>
      {options.options_error && (
        <p className="text-xs text-amber-500">
          Couldn't load the catalog ({options.options_error}).
        </p>
      )}
      <ImageTagPicker
        label="Cryod image tag"
        tags={options.cryod_tags}
        tagsError={options.cryod_tags_error}
        fallback={state.default_image_tag}
        value={imageTag === state.default_image_tag ? "" : imageTag}
        onChange={(next) => setImageTag(next || state.default_image_tag)}
      />
      <div className="space-y-1 text-sm">
        <span className="text-muted-foreground">Tiers{isDefaultTiers && " (the default)"}</span>
        <div className="space-y-1.5">
          {orderedTiers.map((tier) => {
            const info = options.nightly.tier_info[tier];
            const contents = [
              info?.fixed_cells ? `${info.fixed_cells} fixed cells` : "",
              info?.generators.length ? `generators: ${info.generators.join(", ")}` : "",
            ]
              .filter(Boolean)
              .join("; ");
            return (
              <label key={tier} className="flex items-start gap-2">
                <Checkbox
                  className="mt-0.5"
                  checked={tiers.includes(tier)}
                  onCheckedChange={(checked) => setTiers(toggled(tiers, tier, !!checked))}
                />
                <span>
                  <span className="font-mono">{tier}</span>
                  {info?.description && (
                    <span className="text-muted-foreground"> &mdash; {info.description}</span>
                  )}
                  {contents && (
                    <span className="block text-xs text-muted-foreground">{contents}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      {engines.length > 0 && (
        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">
            Engine versions (pre-checked: what runs now; the default pick follows new releases)
          </span>
          {engines.map((engine) => (
            <div key={engine} className="flex flex-wrap items-center gap-3">
              <span className="w-24 text-sm">
                {engine}
                {sameVersions(picked(engine), defaultPick(engine)) &&
                  (run !== "weekend" || shown(engine).length > 0) && (
                    <span className="block text-xs text-muted-foreground">
                      {run === "weekend" ? "all versions" : "the policy's"}
                    </span>
                  )}
              </span>
              {shown(engine).length === 0 ? (
                <span className="text-xs text-muted-foreground">
                  no registry versions listed &mdash;{" "}
                  {run === "weekend"
                    ? "runs every registry version at Friday's launch (the policy's if the registry is down then too: "
                    : "runs the policy's ("}
                  {(policyClasses[engine] ?? []).join(", ")})
                </span>
              ) : (
                shown(engine).map((version) => (
                  <label key={version} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={picked(engine).includes(version)}
                      onCheckedChange={(checked) =>
                        setVersionPicks({
                          ...versionPicks,
                          [engine]: toggled(picked(engine), version, checked === true),
                        })
                      }
                    />
                    <span className="font-mono text-xs">{version}</span>
                  </label>
                ))
              )}
            </div>
          ))}
        </div>
      )}
      {policyOnlyEngines.length > 0 && (
        <p className="text-xs text-muted-foreground">
          These tiers also run{" "}
          {policyOnlyEngines
            .map((engine) => `${engine} (${(policyClasses[engine] ?? []).join(", ")})`)
            .join(", ")}{" "}
          at the policy&apos;s versions.
        </p>
      )}
      <div className="flex items-center gap-3">
        <SubmitButton
          pending={save.isPending}
          disabled={!changed || tiers.length === 0 || noVersions.length > 0}
          label="Save"
          icon={Save}
        />
        {tiers.length === 0 && (
          <span className="text-xs text-destructive">Pick at least one tier.</span>
        )}
        {noVersions.length > 0 && (
          <span className="text-xs text-destructive">
            Pick at least one version for {noVersions.join(", ")}.
          </span>
        )}
        {save.isError && <ErrorText error={save.error} />}
        {save.isSuccess && !changed && (
          <span className="text-xs text-emerald-600">
            Saved &mdash; the next {run === "weekend" ? "weekend run" : "nightly"} runs with these.
          </span>
        )}
      </div>
    </form>
  );
}

const KIND_LABELS: Record<string, string> = {
  "checkpoint-restore": "checkpoint-restore (single node)",
  "cross-restore": "cross-restore",
  "donor-restore": "donor-restore",
};
const ENGINE_LABELS: Record<string, string> = { vllm: "vLLM", sglang: "SGLang", trtllm: "TRT-LLM" };

function pickKey(engine: string, kind: string): string {
  return `${engine}:${kind}`;
}

// `bench cryod ci plan` + `orchestrate`: the release tier, or a custom set —
// the engine × kind grid (every catalog cell of that kind, at the picked
// versions), plus single catalog tests — on my machines (own lane, runs at
// once) or every usable machine (the GPU lane, queued behind a running nightly).
function CryodTestTrigger() {
  const { data: options, isLoading, error } = useTriggerOptions();
  const { data: myMachines, error: machinesError } = useMyMachines();
  const trigger = useTriggerCryodTest();
  const [mode, setMode] = useState<"release" | "custom">("release");
  const [usePool, setUsePool] = useState(false);
  const [machines, setMachines] = useState<string[]>([]);
  const [deadline, setDeadline] = useState("");
  const [imageTag, setImageTag] = useState("");
  const [mooncakeTag, setMooncakeTag] = useState("");
  const [picks, setPicks] = useState<string[]>([]); // "engine:kind"
  const [versions, setVersions] = useState<Record<string, string[]>>({});
  const [tests, setTests] = useState<string[]>([]);
  const [cacheMode, setCacheMode] = useState("tier");
  const [adhoc, setAdhoc] = useState(true);
  const [parallelTests, setParallelTests] = useState("1");
  const [skipEod, setSkipEod] = useState(false);
  const [disposable, setDisposable] = useState(true);
  const startAt = useStartAt();

  const matrix = options?.matrix;
  // Engines with a version axis that a grid pick or a picked test uses.
  const versionedEngines = useMemo(() => {
    const testEngines = new Set(
      (options?.tests ?? []).filter((test) => tests.includes(test.name)).map((test) => test.engine),
    );
    return (matrix?.engines ?? []).filter(
      (engine) =>
        (matrix?.versions[engine]?.length ?? 0) > 0 &&
        (picks.some((pick) => pick.startsWith(`${engine}:`)) || testEngines.has(engine)),
    );
  }, [options, matrix, picks, tests]);
  const hasMachines = usePool || machines.length > 0;
  const hasCells = mode === "release" || picks.length + tests.length > 0;
  const unversioned =
    mode === "custom"
      ? versionedEngines.filter((engine) => pickedVersions(engine).length === 0)
      : [];

  function pickedVersions(engine: string): string[] {
    const known = matrix?.versions[engine] ?? [];
    const latest = matrix?.default_versions[engine] || known[known.length - 1];
    return versions[engine] ?? (latest ? [latest] : []);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cryod test</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <ErrorText error={error} />
        ) : isLoading || options === undefined || matrix === undefined ? (
          <Skeleton className="h-9 w-full" />
        ) : !options.enabled ? (
          <NotConfigured what="Cryod test" />
        ) : (
          <form
            className="space-y-4"
            onKeyDown={noEnterSubmit}
            onSubmit={(event) => {
              event.preventDefault();
              trigger.mutate(
                {
                  mode,
                  machines: usePool ? [] : machines,
                  deadline_hours: Number(deadline) || 0,
                  image_tag: imageTag.trim(),
                  mooncake_image_tag: mooncakeTag.trim(),
                  matrix:
                    mode === "custom"
                      ? picks.map((pick) => {
                          const [engine = "", kind = ""] = pick.split(":");
                          return { engine, kind };
                        })
                      : [],
                  generators: [],
                  tests: mode === "custom" ? tests : [],
                  versions:
                    mode === "custom"
                      ? Object.fromEntries(
                          versionedEngines.map((engine) => [engine, pickedVersions(engine)]),
                        )
                      : {},
                  cache_mode: cacheMode === "warm" || cacheMode === "cold" ? cacheMode : null,
                  adhoc,
                  max_parallel_tests: Number(parallelTests),
                  disposable,
                  skip_eod: skipEod,
                  start_at: startAt.iso,
                },
                { onSuccess: startAt.clear },
              );
            }}
          >
            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Cells</span>
                <Select
                  value={mode}
                  onValueChange={(value) => value && setMode(value as "release" | "custom")}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue>
                      {(current) => (current === "release" ? "Release tier" : "Custom cells")}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="release">Release tier</SelectItem>
                    <SelectItem value="custom">Custom cells</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Deadline (hours, empty = until done)</span>
                <Input
                  type="number"
                  min={0}
                  max={72}
                  step={0.5}
                  className="w-48"
                  value={deadline}
                  onChange={(event) => setDeadline(event.target.value)}
                />
              </label>
              <ImageTagPickers
                options={options}
                imageTag={imageTag}
                onImageTag={setImageTag}
                mooncakeTag={mooncakeTag}
                onMooncakeTag={setMooncakeTag}
              />
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Cache</span>
                <Select value={cacheMode} onValueChange={(value) => value && setCacheMode(value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue>
                      {(current) => (current === "tier" ? "Tier default" : String(current))}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tier">Tier default</SelectItem>
                    <SelectItem value="warm">warm</SelectItem>
                    <SelectItem value="cold">cold</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Parallel tests</span>
                <Select
                  value={parallelTests}
                  onValueChange={(value) => value && setParallelTests(value)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* bench's orchestrator runs at most 2 tests at once */}
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <StartAtPicker startAt={startAt} />
            </div>

            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Machines</span>
              <label className="flex items-center gap-1.5 text-sm">
                <Checkbox
                  checked={usePool}
                  onCheckedChange={(checked) => setUsePool(checked === true)}
                />
                Claim every usable machine
              </label>
              {usePool ? (
                <label className="flex items-center gap-1.5 text-sm">
                  <Checkbox
                    checked={disposable}
                    onCheckedChange={(checked) => setDisposable(checked === true)}
                  />
                  Mark machines it takes from the free pool disposable (anyone may take one)
                </label>
              ) : (
                <MachinePicker
                  machines={myMachines}
                  error={machinesError}
                  picked={machines}
                  onChange={setMachines}
                />
              )}
              <SkipEodCheckbox checked={skipEod} onChange={setSkipEod} />
            </div>

            {mode === "custom" && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <span className="text-sm text-muted-foreground">Cells by engine</span>
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Engine</TableHead>
                          {matrix.kinds.map((kind) => (
                            <TableHead key={kind}>{KIND_LABELS[kind] ?? kind}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matrix.engines.map((engine) => (
                          <TableRow key={engine}>
                            <TableCell className="font-medium">
                              {ENGINE_LABELS[engine] ?? engine}
                            </TableCell>
                            {matrix.kinds.map((kind) => {
                              const count = matrix.counts[engine]?.[kind] ?? 0;
                              const key = pickKey(engine, kind);
                              return (
                                <TableCell key={kind}>
                                  <label className="flex items-center gap-1.5 text-sm">
                                    <Checkbox
                                      disabled={count === 0}
                                      checked={picks.includes(key)}
                                      onCheckedChange={(checked) =>
                                        setPicks(toggled(picks, key, checked === true))
                                      }
                                    />
                                    <span className="text-xs text-muted-foreground">
                                      {count === 0
                                        ? "none"
                                        : `up to ${count * (pickedVersions(engine).length || 1)} cells`}
                                    </span>
                                  </label>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell className="font-medium">Dynamo</TableCell>
                          <TableCell colSpan={matrix.kinds.length}>
                            <label className="flex items-center gap-1.5 text-sm">
                              <Checkbox
                                disabled={(matrix.counts.dynamo?.dynamo ?? 0) === 0}
                                checked={picks.includes(pickKey("dynamo", "dynamo"))}
                                onCheckedChange={(checked) =>
                                  setPicks(
                                    toggled(picks, pickKey("dynamo", "dynamo"), checked === true),
                                  )
                                }
                              />
                              <span className="text-xs text-muted-foreground">
                                up to {matrix.counts.dynamo?.dynamo ?? 0} cells, every engine,
                                runtime's own version
                              </span>
                            </label>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
                {versionedEngines.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-sm text-muted-foreground">
                      Engine versions (default: the latest we support)
                    </span>
                    {versionedEngines.map((engine) => (
                      <div key={engine} className="flex flex-wrap items-center gap-3">
                        <span className="w-16 text-sm">{ENGINE_LABELS[engine] ?? engine}</span>
                        {(matrix.versions[engine] ?? []).map((version) => (
                          <label key={version} className="flex items-center gap-1.5 text-sm">
                            <Checkbox
                              checked={pickedVersions(engine).includes(version)}
                              onCheckedChange={(checked) =>
                                setVersions({
                                  ...versions,
                                  [engine]: toggled(
                                    pickedVersions(engine),
                                    version,
                                    checked === true,
                                  ),
                                })
                              }
                            />
                            <span className="font-mono text-xs">{version}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                <TestPicker
                  label="Single tests"
                  tests={options.tests}
                  picked={tests}
                  onChange={setTests}
                />
              </div>
            )}
            <label className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={adhoc} onCheckedChange={(checked) => setAdhoc(checked === true)} />
              Validation run — results don't count toward coverage or diff baselines
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <SubmitButton
                pending={trigger.isPending}
                disabled={!hasMachines || !hasCells || unversioned.length > 0}
                label={startAt.iso ? "Schedule Cryod test" : "Run Cryod test"}
              />
              {unversioned.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  Pick a version for{" "}
                  {unversioned.map((engine) => ENGINE_LABELS[engine] ?? engine).join(", ")}.
                </span>
              )}
              {!hasMachines && (
                <span className="text-xs text-muted-foreground">
                  Pick machines or every usable machine.
                </span>
              )}
              {!hasCells && (
                <span className="text-xs text-muted-foreground">
                  Pick at least one cell or test.
                </span>
              )}
              {options.options_error && (
                <span className="text-xs text-amber-500">
                  Couldn't load the catalog ({options.options_error}).
                </span>
              )}
              {trigger.isError && <ErrorText error={trigger.error} />}
            </div>
          </form>
        )}
        {trigger.isSuccess && <QueuedNotice queued={trigger.data} runIsJob={false} />}
      </CardContent>
    </Card>
  );
}

// `bench cryod perf run --test <cell>`, one job per picked cell: one checkpoint,
// N timed restores per backend. My machines pair up in pick order (checkpoint
// host, then the peer for the cross-node restores); the jobs spread over the
// pairs and run concurrently, each pair taking its cells one after the other.
function PerfTrigger() {
  const { data: options, isLoading, error } = useTriggerOptions();
  const { data: myMachines, error: machinesError } = useMyMachines();
  const trigger = useTriggerPerf();
  const [machines, setMachines] = useState<string[]>([]);
  const [tests, setTests] = useState<string[]>([]);
  const [versions, setVersions] = useState<Record<string, string>>({});
  const [backends, setBackends] = useState<string[]>(["dram", "tiered_nfs", "tiered_s3", "donor"]);
  const [iterations, setIterations] = useState("10");
  const [imageTag, setImageTag] = useState("");
  const [mooncakeTag, setMooncakeTag] = useState("");
  const [protocol, setProtocol] = useState("rdma");
  const [skipEod, setSkipEod] = useState(false);
  const startAt = useStartAt();

  const perfTests = useMemo(
    () => (options?.tests ?? []).filter((test) => options?.perf_tests.includes(test.name)),
    [options],
  );
  // Engines of the picked cells that have a version axis; the default is the
  // latest we support (the list's last entry).
  const versionedEngines = (options?.matrix.engines ?? []).filter(
    (engine) =>
      (options?.matrix.versions[engine]?.length ?? 0) > 0 &&
      perfTests.some((test) => test.engine === engine && tests.includes(test.name)),
  );
  function pickedVersion(engine: string): string {
    const known = options?.matrix.versions[engine] ?? [];
    return (
      versions[engine] ??
      (options?.matrix.default_versions[engine] || known[known.length - 1] || "")
    );
  }
  const pairs: string[][] = [];
  for (let start = 0; start < machines.length; start += 2) {
    pairs.push(machines.slice(start, start + 2));
  }
  const evenMachines = machines.length > 0 && machines.length % 2 === 0;
  const canSubmit = evenMachines && tests.length > 0 && backends.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance test</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <ErrorText error={error} />
        ) : isLoading || options === undefined ? (
          <Skeleton className="h-9 w-full" />
        ) : !options.enabled ? (
          <NotConfigured what="Performance test" />
        ) : (
          <form
            className="space-y-4"
            onKeyDown={noEnterSubmit}
            onSubmit={(event) => {
              event.preventDefault();
              trigger.mutate(
                {
                  machines,
                  tests,
                  engine_versions: Object.fromEntries(
                    versionedEngines.map((engine) => [engine, pickedVersion(engine)]),
                  ),
                  backends,
                  iterations: Number(iterations) || 10,
                  image_tag: imageTag.trim(),
                  mooncake_image_tag: mooncakeTag.trim(),
                  protocol,
                  skip_eod: skipEod,
                  start_at: startAt.iso,
                },
                { onSuccess: startAt.clear },
              );
            }}
          >
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">
                Machines — pairs in pick order: checkpoint host, then peer (cross-node restores)
              </span>
              <MachinePicker
                machines={myMachines}
                error={machinesError}
                picked={machines}
                onChange={setMachines}
              />
              {pairs.length > 0 && (
                <div className="flex flex-wrap gap-3 font-mono text-xs text-muted-foreground">
                  {pairs.map((pair, index) => (
                    <span key={pair.join("+")}>
                      pair {index + 1}:{" "}
                      {pair.length === 2 ? `${pair[0]} → ${pair[1]}` : `${pair[0]} (needs a peer)`}
                    </span>
                  ))}
                </div>
              )}
              {myMachines !== undefined && myMachines.length < 2 && (
                <span className="text-xs text-amber-500">You need to hold two machines.</span>
              )}
              <SkipEodCheckbox checked={skipEod} onChange={setSkipEod} />
            </div>
            <TestPicker
              label="Cells (one job each)"
              tests={perfTests}
              picked={tests}
              onChange={setTests}
            />
            {versionedEngines.length > 0 && (
              <div className="flex flex-wrap items-end gap-3">
                {versionedEngines.map((engine) => (
                  <label key={engine} className="space-y-1 text-sm">
                    <span className="text-muted-foreground">
                      {ENGINE_LABELS[engine] ?? engine} version
                    </span>
                    <Select
                      value={pickedVersion(engine)}
                      onValueChange={(value) =>
                        value && setVersions({ ...versions, [engine]: value })
                      }
                    >
                      <SelectTrigger className="w-40 font-mono">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...(options.matrix.versions[engine] ?? [])].reverse().map((version) => (
                          <SelectItem key={version} value={version}>
                            {version}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1 text-sm">
                <span className="text-muted-foreground">Backends</span>
                <div className="flex flex-wrap gap-3">
                  {options.backends.map((backend) => (
                    <label key={backend} className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={backends.includes(backend)}
                        onCheckedChange={(checked) =>
                          setBackends(toggled(backends, backend, checked === true))
                        }
                      />
                      {PERF_BACKEND_LABELS[backend] ?? backend}
                    </label>
                  ))}
                </div>
              </div>
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Restores per backend</span>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  className="w-24"
                  value={iterations}
                  onChange={(event) => setIterations(event.target.value)}
                />
              </label>
              <ImageTagPickers
                options={options}
                imageTag={imageTag}
                onImageTag={setImageTag}
                mooncakeTag={mooncakeTag}
                onMooncakeTag={setMooncakeTag}
              />
              <label className="space-y-1 text-sm">
                <span className="text-muted-foreground">Mooncake protocol</span>
                <Select value={protocol} onValueChange={(value) => value && setProtocol(value)}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.protocols.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <StartAtPicker startAt={startAt} />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SubmitButton
                pending={trigger.isPending}
                disabled={!canSubmit}
                label={`${startAt.iso ? "Schedule" : "Run"} ${
                  tests.length > 1 ? `${tests.length} performance tests` : "performance test"
                }`}
              />
              {!evenMachines && (
                <span className="text-xs text-muted-foreground">Pick machines in pairs.</span>
              )}
              {tests.length === 0 && (
                <span className="text-xs text-muted-foreground">Pick at least one cell.</span>
              )}
              {trigger.isError && <ErrorText error={trigger.error} />}
            </div>
          </form>
        )}
        {trigger.isSuccess && <QueuedNotice queued={trigger.data} />}
      </CardContent>
    </Card>
  );
}

const TIME_FILTERS = [
  { label: "All", value: "all" },
  { label: "Last 24h", value: "24h" },
  { label: "Last 7d", value: "7d" },
  { label: "Last 30d", value: "30d" },
  { label: "Custom range", value: "custom" },
] as const;

const TIME_HOURS: Record<string, number> = { "24h": 24, "7d": 168, "30d": 720 };

function jobTimestamp(job: CiJobOverview): number {
  const iso = job.created_at;
  return iso ? new Date(iso).getTime() : 0;
}

function RecentJobs({
  trigger,
  label,
  refLabel,
}: {
  trigger: string;
  label: string;
  refLabel: string;
}) {
  const { formatShortTimestamp } = useTimezone();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cancel = useCancelCiJob();
  const [requestedBy, setRequestedBy] = useState("all");
  const [branch, setBranch] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  // The time window goes to the server (`since` / `until`), so it is
  // authoritative rather than "within the newest N rows". Presets are rounded
  // to the minute so the query key doesn't change on every render.
  const queryParams = useMemo(() => {
    const params: Record<string, string> = { trigger, limit: "50" };
    const hours = TIME_HOURS[timeFilter];
    if (hours !== undefined) {
      const cutoff = Math.floor((Date.now() - hours * 3_600_000) / 60_000) * 60_000;
      params.since = new Date(cutoff).toISOString();
    } else if (timeFilter === "custom") {
      if (customFrom) params.since = `${customFrom}T00:00:00Z`;
      if (customTo) params.until = `${customTo}T23:59:59Z`;
    }
    return params;
  }, [trigger, timeFilter, customFrom, customTo]);
  const { data: jobs } = useCiJobOverviews(queryParams);

  const all = useMemo(() => jobs ?? [], [jobs]);
  // The time window is applied first, with a live clock, so the option lists
  // and the "N of M" counter describe the same rows the table can show.
  const inWindow = useMemo(() => {
    const hours = TIME_HOURS[timeFilter];
    if (hours !== undefined) {
      const cutoff = Date.now() - hours * 3_600_000;
      return all.filter((job) => jobTimestamp(job) >= cutoff);
    }
    if (timeFilter === "custom") {
      const from = customFrom ? new Date(`${customFrom}T00:00:00Z`).getTime() : null;
      const to = customTo ? new Date(`${customTo}T23:59:59Z`).getTime() : null;
      return all.filter((job) => {
        const timestamp = jobTimestamp(job);
        return (from === null || timestamp >= from) && (to === null || timestamp <= to);
      });
    }
    return all;
  }, [all, timeFilter, customFrom, customTo]);
  const requesters = useMemo(
    () => [...new Set(inWindow.map((job) => job.requested_by).filter(Boolean) as string[])].sort(),
    [inWindow],
  );
  const branches = useMemo(
    () => [...new Set(inWindow.map((job) => job.release_tag ?? "main"))].sort(),
    [inWindow],
  );
  // A pick that the current window no longer offers falls back to "all"
  // instead of stranding the table on an empty result. The pick itself is
  // kept, so it applies again once a wider window offers it.
  const requestedByPick = requesters.includes(requestedBy) ? requestedBy : "all";
  const branchPick = branches.includes(branch) ? branch : "all";
  const recent = useMemo(() => {
    let rows = inWindow;
    if (requestedByPick !== "all")
      rows = rows.filter((job) => job.requested_by === requestedByPick);
    if (branchPick !== "all")
      rows = rows.filter((job) => (job.release_tag ?? "main") === branchPick);
    return rows;
  }, [inWindow, requestedByPick, branchPick]);

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-medium">Recent {label} jobs</h2>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={requestedByPick} onValueChange={(value) => value && setRequestedBy(value)}>
          <SelectTrigger className="w-56 gap-1">
            <span className="text-muted-foreground">Requested by:</span>
            <SelectValue>
              {(current) => (current === "all" ? "Anyone" : String(current))}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anyone</SelectItem>
            {requesters.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={branchPick} onValueChange={(value) => value && setBranch(value)}>
          <SelectTrigger className="w-64 gap-1">
            <span className="text-muted-foreground">{refLabel}:</span>
            <SelectValue>{(current) => (current === "all" ? "All" : String(current))}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {branches.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={timeFilter} onValueChange={(value) => value && setTimeFilter(value)}>
          <SelectTrigger className="w-48 gap-1">
            <span className="text-muted-foreground">Time:</span>
            <SelectValue>
              {(current) => TIME_FILTERS.find((option) => option.value === current)?.label ?? "All"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {TIME_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {timeFilter === "custom" && (
          <div className="flex items-center gap-1.5 text-sm">
            <input
              type="date"
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
              className="rounded-md border bg-transparent px-2 py-1 text-sm"
            />
            <span className="text-muted-foreground">–</span>
            <input
              type="date"
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
              className="rounded-md border bg-transparent px-2 py-1 text-sm"
            />
          </div>
        )}
        {recent.length !== inWindow.length && (
          <span className="text-sm text-muted-foreground">
            {recent.length} of {inWindow.length} jobs
          </span>
        )}
        {cancel.isError && <ErrorText error={cancel.error} />}
      </div>
      {jobs === undefined ? (
        <Skeleton className="h-32 w-full" />
      ) : recent.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {all.length === 0 &&
          (timeFilter === "all" || (timeFilter === "custom" && !customFrom && !customTo))
            ? `No ${label} jobs yet.`
            : "No jobs match the filters."}
        </p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Queued</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>{refLabel}</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Requested by</TableHead>
                <TableHead className="text-right">Results</TableHead>
                {trigger === "cryod-test" && <TableHead>CI run</TableHead>}
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((job) => (
                <TableRow
                  key={job.job_id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/ci/jobs/${job.job_id}`)}
                >
                  <TableCell className="font-medium">
                    {job.created_at ? formatShortTimestamp(job.created_at) : "-"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{job.job_id}</TableCell>
                  <TableCell className="font-mono text-xs">{job.release_tag ?? "main"}</TableCell>
                  <TableCell>
                    <CiStatusBadge status={job.state} />
                    {job.state === "queued" && job.not_before && (
                      <div className="text-xs text-muted-foreground">
                        starts {formatShortTimestamp(job.not_before)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{job.requested_by ?? "-"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {job.rollup.total === 0 ? (
                      <span className="text-muted-foreground">-</span>
                    ) : (
                      <>
                        <span className="text-emerald-500">{job.rollup.passed}✓</span>
                        {job.rollup.failed > 0 && (
                          <span className="ml-1.5 text-rose-500">{job.rollup.failed}✗</span>
                        )}
                        {job.rollup.skipped > 0 && (
                          <span className="ml-1.5 text-muted-foreground">
                            {job.rollup.skipped}s
                          </span>
                        )}
                      </>
                    )}
                  </TableCell>
                  {trigger === "cryod-test" && (
                    <TableCell>
                      {job.rollup.total > 0 ? (
                        <Link
                          className="text-xs underline"
                          to={`/ci/jobs/${job.job_id}`}
                          onClick={(event) => event.stopPropagation()}
                        >
                          on the job page
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground">not published yet</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    {ACTIVE_JOB_STATES.has(job.state) &&
                      (job.requested_by === user?.username || user?.is_admin) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            job.cancel_requested_at !== null ||
                            (cancel.isPending && cancel.variables === job.job_id)
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            cancel.mutate(job.job_id);
                          }}
                        >
                          <Square className="mr-1.5 size-3" />
                          {job.cancel_requested_at ? "Stopping…" : "Stop"}
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
